/**
 * Sidebar Component - Accordion style with collapsible sections
 */
import React, { useState, useCallback } from 'react';
import { ProjectList } from '../Sidebar/ProjectList';
import { PoleList } from '../Sidebar/PoleList';
import { CableList } from '../Sidebar/CableList';
import { StatisticsPanel } from '../Panels/StatisticsPanel';
import { ValidationPanel } from '../Panels/ValidationPanel';
import { SettingsDialog } from '../Dialogs/SettingsDialog';
import { useSidebarStore } from '../../store/sidebarStore';
import { useProjectStore, usePoleStore, useCableStore } from '../../stores';
import './Sidebar.css';

type Section = 'projects' | 'poles' | 'cables' | 'stats';

export const Sidebar: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<Set<Section>>(new Set<Section>(['projects']));
  const [showSettings, setShowSettings] = useState(false);
  const { isOpen } = useSidebarStore();
  const { currentProject, projects } = useProjectStore();
  const { poles } = usePoleStore();
  const { cables } = useCableStore();

  const toggleSection = (section: Section) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const isExpanded = (section: Section) => expandedSections.has(section);

  const handleOpenSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  return (
    <aside className={`sidebar accordion-style ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-accordion">
        {/* Projects Section */}
        <div className={`accordion-section ${isExpanded('projects') ? 'expanded' : ''}`}>
          <button
            className="accordion-header"
            onClick={() => toggleSection('projects')}
          >
            <div className="accordion-header-content">
              <span className="accordion-icon">📁</span>
              <span className="accordion-title">Projekty</span>
              <span className="accordion-badge">{projects.length}</span>
            </div>
            <span className="accordion-chevron">{isExpanded('projects') ? '▼' : '▶'}</span>
          </button>
          {isExpanded('projects') && (
            <div className="accordion-content">
              <ProjectList />
            </div>
          )}
        </div>

        {/* Poles Section */}
        <div className={`accordion-section ${isExpanded('poles') ? 'expanded' : ''}`}>
          <button
            className="accordion-header"
            onClick={() => toggleSection('poles')}
            disabled={!currentProject}
          >
            <div className="accordion-header-content">
              <span className="accordion-icon">📍</span>
              <span className="accordion-title">Stĺpiky</span>
              {currentProject && <span className="accordion-badge">{poles.length}</span>}
            </div>
            <span className="accordion-chevron">{isExpanded('poles') ? '▼' : '▶'}</span>
          </button>
          {isExpanded('poles') && currentProject && (
            <div className="accordion-content">
              <PoleList />
            </div>
          )}
        </div>

        {/* Cables Section */}
        <div className={`accordion-section ${isExpanded('cables') ? 'expanded' : ''}`}>
          <button
            className="accordion-header"
            onClick={() => toggleSection('cables')}
            disabled={!currentProject}
          >
            <div className="accordion-header-content">
              <span className="accordion-icon">🔌</span>
              <span className="accordion-title">Káble</span>
              {currentProject && <span className="accordion-badge">{cables.length}</span>}
            </div>
            <span className="accordion-chevron">{isExpanded('cables') ? '▼' : '▶'}</span>
          </button>
          {isExpanded('cables') && currentProject && (
            <div className="accordion-content">
              <CableList />
            </div>
          )}
        </div>

        {/* Statistics Section */}
        <div className={`accordion-section ${isExpanded('stats') ? 'expanded' : ''}`}>
          <button
            className="accordion-header"
            onClick={() => toggleSection('stats')}
            disabled={!currentProject}
          >
            <div className="accordion-header-content">
              <span className="accordion-icon">📊</span>
              <span className="accordion-title">Štatistika</span>
            </div>
            <span className="accordion-chevron">{isExpanded('stats') ? '▼' : '▶'}</span>
          </button>
          {isExpanded('stats') && currentProject && (
            <div className="accordion-content">
              <button
                className="network-design-button"
                onClick={handleOpenSettings}
                style={{ marginBottom: '16px' }}
              >
                ⚙️ Nastavenia a Záloha
              </button>
              <StatisticsPanel />
              <ValidationPanel />
            </div>
          )}
        </div>
      </div>

      <SettingsDialog
        isOpen={showSettings}
        onClose={handleCloseSettings}
      />
    </aside>
  );
};
